import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Users, FileCheck, Clock, Shield, CheckCircle2, XCircle, Loader2, ChevronDown, ChevronUp, AlertCircle, Download, Calendar, CalendarCheck, LogIn, LogOut as LogOutIcon, X, ClipboardEdit, Timer } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import RMLayout from '@/components/RMLayout';
import { rmAPI, sharedAPI } from '@/lib/api';
import { formatDate } from '@/lib/dateUtils';

const PATH_TAB_MAP = {
  '/rm': 'overview',
  '/rm/employees': 'overview',
  '/rm/attendance': 'attendance',
  '/rm/leaves': 'leaves',
  '/rm/sanctions': 'sanctions',
  '/rm/permits': 'permits',
  '/rm/reports': 'reports',
};

export default function RMDashboard() {
  const location = useLocation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState(() => PATH_TAB_MAP[location.pathname] || 'overview');
  const [actionLoading, setActionLoading] = useState('');
  const [showOnLeave, setShowOnLeave] = useState(false);
  const [onLeaveList, setOnLeaveList] = useState([]);
  const [onLeaveLoading, setOnLeaveLoading] = useState(false);

  useEffect(() => { fetchDashboard(); }, []);

  useEffect(() => {
    const mapped = PATH_TAB_MAP[location.pathname];
    if (mapped) setTab(mapped);
  }, [location.pathname]);

  const fetchDashboard = async () => {
    setLoading(true);
    try {
      const res = await rmAPI.getDashboard();
      setData(res.data);
    } catch (err) {
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  const handleAction = async (type, id, status) => {
    setActionLoading(`${type}-${id}-${status}`);
    try {
      if (type === 'leave') await rmAPI.updateLeave(id, status);
      else if (type === 'sanction') await rmAPI.updateSanction(id, status);
      else if (type === 'permit') await rmAPI.updatePermit(id, status);
      else if (type === 'comp_off') await rmAPI.updateCompOff(id, status);
      toast.success(`Request ${status}`);
      fetchDashboard();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Action failed');
    } finally {
      setActionLoading('');
    }
  };

  const fetchOnLeaveToday = async () => {
    setOnLeaveLoading(true);
    try {
      const res = await sharedAPI.onLeaveToday();
      setOnLeaveList(res.data.on_leave || []);
      setShowOnLeave(true);
    } catch {
      toast.error('Failed to load on-leave data');
    } finally {
      setOnLeaveLoading(false);
    }
  };

  const counts = data?.counts || {};
  const totalPending = (counts.pending_leaves || 0) + (counts.pending_sanctions || 0) + (counts.pending_permits || 0);

  const tabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'attendance', label: 'Attendance' },
    { id: 'leaves', label: 'Leaves', count: counts.pending_leaves },
    { id: 'sanctions', label: 'Late Sanctions', count: counts.pending_sanctions },
    { id: 'permits', label: 'Work Permits', count: counts.pending_permits },
    { id: 'overrides', label: 'Overrides' },
    { id: 'comp_offs', label: 'Comp-Off', count: counts.pending_comp_offs },
    { id: 'reports', label: 'Reports' },
  ];

  if (loading) {
    return (
      <RMLayout>
        <div className="flex items-center justify-center py-20">
          <div className="h-8 w-8 border-2 border-[#026536] border-t-transparent rounded-full animate-spin" />
        </div>
      </RMLayout>
    );
  }

  return (
    <RMLayout>
      <div className="space-y-6 animate-fade-in">
        {/* Header */}
        <div>
          <h1 data-testid="rm-dashboard-title" className="text-xl font-extrabold text-[#1a2529] tracking-tight">
            Welcome, {data?.rm_name}
          </h1>
          <p className="text-sm text-[#1a2529]/50 mt-0.5">Reporting Manager Dashboard</p>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <StatCard icon={<Users size={18} />} label="My Team" value={counts.total_employees} color="text-[#026536]" bg="bg-[#026536]/10" />
          <StatCard icon={<FileCheck size={18} />} label="Pending Leaves" value={counts.pending_leaves} color="text-[#dea439]" bg="bg-[#dea439]/10" />
          <StatCard icon={<Clock size={18} />} label="Late Sanctions" value={counts.pending_sanctions} color="text-purple-600" bg="bg-purple-50" />
          <StatCard icon={<Shield size={18} />} label="Work Permits" value={counts.pending_permits} color="text-blue-600" bg="bg-blue-50" />
        </div>

        {/* Tabs */}
        <div className="flex gap-1 bg-white rounded-xl p-1 shadow-sm border border-border/30 overflow-x-auto scrollbar-hide -mx-4 px-4 lg:mx-0 lg:px-0">
          {tabs.map(t => (
            <button
              key={t.id}
              data-testid={`rm-tab-${t.id}`}
              onClick={() => setTab(t.id)}
              className={`shrink-0 py-2 px-3 rounded-lg text-xs font-bold transition-colors relative whitespace-nowrap ${tab === t.id ? 'bg-[#026536] text-white' : 'text-[#1a2529]/50 hover:text-[#1a2529]'}`}
            >
              {t.label}
              {t.count > 0 && (
                <span className={`ml-1 inline-flex items-center justify-center h-4 min-w-[16px] rounded-full text-[9px] font-bold px-1 ${tab === t.id ? 'bg-white/20 text-white' : 'bg-red-500 text-white'}`}>
                  {t.count}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Content */}
        {tab === 'overview' && (
          <div className="space-y-4">
            {/* Team Members */}
            <div className="bg-white rounded-xl border border-border/30 shadow-sm">
              <div className="px-4 py-3 border-b border-border/20">
                <h3 className="font-bold text-sm text-[#1a2529]">Team Members ({data?.employees?.length})</h3>
              </div>
              <div className="divide-y divide-border/10">
                {data?.employees?.map(emp => (
                  <div key={emp.id} data-testid={`rm-emp-${emp.id}`} className="px-4 py-3 flex items-center justify-between">
                    <div>
                      <p className="font-semibold text-sm text-[#1a2529]">{emp.name}</p>
                      <p className="text-[11px] text-[#1a2529]/40">{emp.phone} {emp.designation ? `| ${emp.designation}` : ''}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs font-bold text-[#026536]">{emp.leave_balance} leaves left</p>
                      <p className="text-[10px] text-[#1a2529]/30">of {emp.total_leaves}</p>
                    </div>
                  </div>
                ))}
                {(!data?.employees || data.employees.length === 0) && (
                  <div className="px-4 py-8 text-center text-[#1a2529]/40 text-sm">No employees assigned</div>
                )}
              </div>
            </div>

            {/* Pending Actions Summary */}
            {totalPending > 0 && (
              <div className="bg-[#dea439]/5 border border-[#dea439]/20 rounded-xl p-4 flex items-center gap-3">
                <AlertCircle size={20} className="text-[#dea439] shrink-0" />
                <div>
                  <p className="font-bold text-sm text-[#1a2529]">{totalPending} pending request{totalPending > 1 ? 's' : ''}</p>
                  <p className="text-[11px] text-[#1a2529]/50">Use tabs above to review and take action</p>
                </div>
              </div>
            )}

            {/* On Leave Today */}
            <button
              data-testid="rm-on-leave-today-btn"
              onClick={fetchOnLeaveToday}
              disabled={onLeaveLoading}
              className="w-full bg-white rounded-xl border border-border/30 shadow-sm p-4 flex items-center justify-between hover:bg-[#f4f4f5] transition-colors"
            >
              <div className="flex items-center gap-3">
                <div className="h-9 w-9 rounded-lg bg-purple-50 flex items-center justify-center">
                  <Users size={16} className="text-purple-600" />
                </div>
                <div className="text-left">
                  <p className="text-sm font-bold text-[#1a2529]">On Leave Today</p>
                  <p className="text-[10px] text-[#1a2529]/40">See who's on leave across team</p>
                </div>
              </div>
              {onLeaveLoading ? <Loader2 size={16} className="animate-spin text-[#1a2529]/30" /> : <ChevronDown size={16} className="-rotate-90 text-[#1a2529]/30" />}
            </button>
          </div>
        )}

        {/* On Leave Today Modal */}
        {showOnLeave && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => setShowOnLeave(false)}>
            <div className="bg-white w-full max-w-lg rounded-2xl max-h-[70vh] overflow-hidden animate-fade-in mx-4" onClick={(e) => e.stopPropagation()}>
              <div className="px-5 py-4 border-b border-border/20 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Users size={16} className="text-purple-600" />
                  <h3 className="font-bold text-sm text-[#1a2529]">On Leave Today</h3>
                  <Badge className="bg-purple-50 text-purple-600 border-0 text-[10px]">{onLeaveList.length}</Badge>
                </div>
                <button data-testid="rm-close-on-leave-modal" onClick={() => setShowOnLeave(false)} className="h-7 w-7 rounded-full bg-[#f4f4f5] flex items-center justify-center">
                  <X size={14} className="text-[#1a2529]/60" />
                </button>
              </div>
              <div className="overflow-y-auto max-h-[55vh] p-4">
                {onLeaveList.length === 0 ? (
                  <div className="text-center py-8">
                    <CheckCircle2 className="mx-auto text-[#026536]/20 mb-2" size={28} />
                    <p className="text-sm font-medium text-[#1a2529]/40">No one is on leave today</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {onLeaveList.map((person, idx) => (
                      <div key={`${person.name}-${idx}`} data-testid={`rm-on-leave-person-${idx}`} className="bg-[#f4f4f5] rounded-xl p-3 flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="h-8 w-8 rounded-full bg-purple-100 flex items-center justify-center text-xs font-bold text-purple-600">
                            {person.name?.[0]?.toUpperCase()}
                          </div>
                          <div>
                            <p className="text-sm font-semibold text-[#1a2529]">{person.name}</p>
                            <p className="text-[10px] text-[#1a2529]/40">
                              {person.role === 'employee' ? 'Staff' : person.role?.toUpperCase()}
                              {person.reporting_manager ? ` | RM: ${person.reporting_manager}` : ''}
                            </p>
                          </div>
                        </div>
                        <Badge className="bg-purple-50 text-purple-600 border-0 text-[10px]">{person.duration}</Badge>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {tab === 'attendance' && <StaffAttendanceTab />}

        {tab === 'leaves' && (
          <RequestList
            title="Leave Requests"
            items={data?.all_requests?.leaves || []}
            pending={data?.pending?.leaves || []}
            type="leave"
            onAction={handleAction}
            actionLoading={actionLoading}
            renderItem={(item) => (
              <div>
                <p className="text-xs text-[#1a2529]/60">
                  {item.leave_type} | {item.duration_type === 'half_day' ? 'Half Day' : item.duration_type === 'hours' ? `${item.hours}h` : `${item.days} day${item.days > 1 ? 's' : ''}`} | {formatDate(item.start_date)}{item.start_date !== item.end_date ? ` - ${formatDate(item.end_date)}` : ''}
                  {item.is_lwp && <span className="ml-1.5 inline-block bg-orange-50 text-orange-600 text-[9px] font-bold px-1.5 py-0.5 rounded">LWP</span>}
                </p>
                {item.reason && <p className="text-[11px] text-[#1a2529]/40 mt-0.5">{item.reason}</p>}
                {item.employee_total_leaves != null && (
                  <div className="mt-1.5 flex items-center gap-2">
                    <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full transition-all ${item.employee_leave_balance <= 2 ? 'bg-red-400' : item.employee_leave_balance <= 5 ? 'bg-[#dea439]' : 'bg-[#026536]'}`}
                        style={{ width: `${Math.max(0, Math.min(100, (item.employee_leave_balance / item.employee_total_leaves) * 100))}%` }}
                      />
                    </div>
                    <span className={`text-[10px] font-bold ${item.employee_leave_balance <= 2 ? 'text-red-500' : 'text-[#1a2529]/50'}`}>
                      {item.employee_leave_balance}/{item.employee_total_leaves} left
                    </span>
                  </div>
                )}
              </div>
            )}
          />
        )}

        {tab === 'sanctions' && (
          <SanctionsView
            allItems={data?.all_requests?.sanctions || []}
            pendingItems={data?.pending?.sanctions || []}
            onAction={handleAction}
            actionLoading={actionLoading}
          />
        )}

        {tab === 'permits' && (
          <RequestList
            title="Work Permit Requests"
            items={data?.all_requests?.permits || []}
            pending={data?.pending?.permits || []}
            type="permit"
            onAction={handleAction}
            actionLoading={actionLoading}
            renderItem={(item) => (
              <div>
                <p className="text-xs text-[#1a2529]/60">
                  {item.permission_type === 'wfh' ? 'Work from Home' : item.permission_type?.replace('_', ' ')} | {formatDate(item.date)}
                  {item.hours && item.permission_type !== 'wfh' ? ` | ${item.hours}h` : ''}
                </p>
                {item.reason && <p className="text-[11px] text-[#1a2529]/40 mt-0.5">{item.reason}</p>}
              </div>
            )}
          />
        )}

        {tab === 'overrides' && <OverridesTab employees={data?.employees || []} />}

        {tab === 'comp_offs' && <CompOffApprovalSection pending={data?.pending?.comp_offs || []} all={data?.all_requests?.comp_offs || []} onAction={handleAction} actionLoading={actionLoading} />}

        {tab === 'reports' && <ReportsTab />}
      </div>
    </RMLayout>
  );
}

function StatCard({ icon, label, value, color, bg }) {
  return (
    <div data-testid={`rm-stat-${label.toLowerCase().replace(/\s/g, '-')}`} className={`${bg} rounded-xl p-4`}>
      <div className={`flex items-center gap-2 ${color} mb-1`}>
        {icon}
        <span className="text-2xl font-extrabold">{value}</span>
      </div>
      <p className={`text-[10px] font-bold uppercase ${color} opacity-50`}>{label}</p>
    </div>
  );
}

function RequestList({ title, items, pending, type, onAction, actionLoading, renderItem }) {
  const [showAll, setShowAll] = useState(false);
  const pendingIds = new Set(pending.map(p => p.id));
  const displayItems = showAll ? items : pending;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="font-bold text-sm text-[#1a2529]">{title}</h3>
        <button onClick={() => setShowAll(!showAll)} className="text-xs font-semibold text-[#026536] flex items-center gap-1">
          {showAll ? <>Pending Only <ChevronUp size={14} /></> : <>Show All <ChevronDown size={14} /></>}
        </button>
      </div>
      {displayItems.length === 0 ? (
        <div className="bg-white rounded-xl p-8 text-center border border-border/30 shadow-sm">
          <CheckCircle2 className="mx-auto text-[#026536]/20 mb-2" size={32} />
          <p className="text-sm font-medium text-[#1a2529]/40">
            {showAll ? 'No requests yet' : 'No pending requests'}
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {displayItems.map(item => (
            <div key={item.id} data-testid={`rm-${type}-${item.id}`} className="bg-white rounded-xl p-4 border border-border/30 shadow-sm">
              <div className="flex items-start justify-between mb-2">
                <div>
                  <p className="font-bold text-sm text-[#1a2529]">{item.user_name}</p>
                  <p className="text-[10px] text-[#1a2529]/30">{item.user_phone}</p>
                </div>
                <Badge className={`border-0 text-[10px] ${item.status === 'approved' ? 'bg-[#026536]/10 text-[#026536]' : item.status === 'rejected' ? 'bg-red-50 text-red-500' : 'bg-[#dea439]/10 text-[#dea439]'}`}>
                  {item.status}
                  {item.approved_by_role === 'rm' && item.status !== 'pending' && <span className="ml-1 opacity-60">(RM)</span>}
                </Badge>
              </div>
              {renderItem(item)}
              {pendingIds.has(item.id) && (
                <div className="flex gap-2 mt-3 pt-2 border-t border-border/10">
                  <Button
                    data-testid={`rm-approve-${type}-${item.id}`}
                    size="sm"
                    onClick={() => onAction(type, item.id, 'approved')}
                    disabled={!!actionLoading}
                    className="bg-[#026536] hover:bg-[#026536]/90 text-xs h-8 gap-1 font-semibold"
                  >
                    {actionLoading === `${type}-${item.id}-approved` ? <Loader2 size={12} className="animate-spin" /> : <CheckCircle2 size={12} />}
                    Approve
                  </Button>
                  <Button
                    data-testid={`rm-reject-${type}-${item.id}`}
                    size="sm"
                    variant="outline"
                    onClick={() => onAction(type, item.id, 'rejected')}
                    disabled={!!actionLoading}
                    className="text-red-500 border-red-200 hover:bg-red-50 text-xs h-8 gap-1 font-semibold"
                  >
                    {actionLoading === `${type}-${item.id}-rejected` ? <Loader2 size={12} className="animate-spin" /> : <XCircle size={12} />}
                    Reject
                  </Button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


function SanctionsView({ allItems, pendingItems, onAction, actionLoading }) {
  const [showAll, setShowAll] = useState(false);
  const todayStr = new Date().toISOString().split('T')[0];
  const todayPending = pendingItems.filter(s => (s.attendance_date || s.date) === todayStr);
  const todayResolved = allItems.filter(s => (s.attendance_date || s.date) === todayStr && s.status !== 'pending');
  const pastPending = pendingItems.filter(s => (s.attendance_date || s.date) !== todayStr);
  const pastResolved = allItems.filter(s => (s.attendance_date || s.date) !== todayStr && s.status !== 'pending');

  const pendingIds = new Set(pendingItems.map(p => p.id));

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-bold text-sm text-[#1a2529]">Late Sanction Requests</h3>
        <button onClick={() => setShowAll(!showAll)} className="text-xs font-semibold text-[#026536] flex items-center gap-1">
          {showAll ? <>Pending Only <ChevronUp size={14} /></> : <>Show All <ChevronDown size={14} /></>}
        </button>
      </div>

      {/* Today Pending */}
      {todayPending.length > 0 && (
        <div>
          <div className="flex items-center gap-2 mb-2">
            <div className="h-1.5 w-1.5 rounded-full bg-[#dea439] animate-pulse" />
            <span className="text-[10px] font-extrabold text-[#dea439] uppercase">Today - Action Required ({todayPending.length})</span>
          </div>
          <div className="space-y-2">
            {todayPending.map(item => (
              <SanctionActionCard key={item.id} item={item} onAction={onAction} actionLoading={actionLoading} prominent />
            ))}
          </div>
        </div>
      )}

      {/* Today Resolved */}
      {showAll && todayResolved.length > 0 && (
        <div>
          <span className="text-[10px] font-bold text-[#1a2529]/30 uppercase">Today - Resolved</span>
          <div className="space-y-1 mt-1">
            {todayResolved.map(item => (
              <CompactRow key={item.id} item={item} />
            ))}
          </div>
        </div>
      )}

      {/* Past Pending */}
      {pastPending.length > 0 && (
        <div>
          <span className="text-[10px] font-bold text-[#1a2529]/30 uppercase">Past - Pending ({pastPending.length})</span>
          <div className="space-y-2 mt-2">
            {pastPending.map(item => (
              <SanctionActionCard key={item.id} item={item} onAction={onAction} actionLoading={actionLoading} />
            ))}
          </div>
        </div>
      )}

      {/* Past Resolved */}
      {showAll && pastResolved.length > 0 && (
        <div>
          <span className="text-[10px] font-bold text-[#1a2529]/30 uppercase">Past - Resolved ({pastResolved.length})</span>
          <div className="space-y-1 mt-1">
            {pastResolved.map(item => (
              <CompactRow key={item.id} item={item} />
            ))}
          </div>
        </div>
      )}

      {pendingItems.length === 0 && !showAll && (
        <div className="bg-white rounded-xl p-8 text-center border border-border/30">
          <CheckCircle2 className="mx-auto text-[#026536]/20 mb-2" size={28} />
          <p className="text-sm font-medium text-[#1a2529]/40">No pending sanctions</p>
        </div>
      )}

      {showAll && allItems.length === 0 && (
        <div className="bg-white rounded-xl p-8 text-center border border-border/30">
          <p className="text-sm font-medium text-[#1a2529]/40">No sanction requests yet</p>
        </div>
      )}
    </div>
  );
}

function SanctionActionCard({ item, onAction, actionLoading, prominent }) {
  const dateField = item.attendance_date || item.date || '';
  return (
    <div data-testid={`rm-sanction-${item.id}`} className={`rounded-xl p-4 border shadow-sm ${prominent ? 'bg-[#dea439]/5 border-[#dea439]/20' : 'bg-white border-border/30'}`}>
      <div className="flex items-center justify-between mb-2">
        <div>
          <p className="font-bold text-sm text-[#1a2529]">{item.user_name}</p>
          <p className="text-[10px] text-[#1a2529]/30">{item.user_phone}</p>
        </div>
        <Badge className="bg-[#dea439]/10 text-[#dea439] border-0 text-[10px]">{formatDate(dateField)}</Badge>
      </div>
      {item.reason && <p className="text-[11px] text-[#1a2529]/50 mb-3 italic">"{item.reason}"</p>}
      <div className="flex gap-2">
        <Button data-testid={`rm-approve-sanction-${item.id}`} size="sm" onClick={() => onAction('sanction', item.id, 'approved')}
          disabled={!!actionLoading} className="flex-1 h-8 bg-[#026536] hover:bg-[#026536]/90 text-xs font-semibold gap-1">
          {actionLoading === `sanction-${item.id}-approved` ? <Loader2 size={12} className="animate-spin" /> : <CheckCircle2 size={12} />} Approve
        </Button>
        <Button data-testid={`rm-reject-sanction-${item.id}`} size="sm" variant="outline" onClick={() => onAction('sanction', item.id, 'rejected')}
          disabled={!!actionLoading} className="flex-1 h-8 border-red-200 text-red-500 hover:bg-red-50 text-xs font-semibold gap-1">
          {actionLoading === `sanction-${item.id}-rejected` ? <Loader2 size={12} className="animate-spin" /> : <XCircle size={12} />} Reject
        </Button>
      </div>
    </div>
  );
}

function CompactRow({ item }) {
  const dateField = item.attendance_date || item.date || '';
  return (
    <div className="bg-white rounded-lg px-3 py-2 border border-border/10 flex items-center justify-between">
      <div className="flex items-center gap-2 min-w-0">
        <div className="h-6 w-6 rounded-full bg-[#f4f4f5] flex items-center justify-center text-[9px] font-bold text-[#1a2529]/30 shrink-0">{item.user_name?.[0]}</div>
        <span className="text-xs font-semibold text-[#1a2529] truncate">{item.user_name}</span>
        <span className="text-[10px] text-[#1a2529]/30">{formatDate(dateField)}</span>
      </div>
      <Badge className={`border-0 text-[9px] font-bold capitalize ${item.status === 'approved' ? 'bg-[#026536]/10 text-[#026536]' : 'bg-red-50 text-red-500'}`}>
        {item.status}
      </Badge>
    </div>
  );
}


function StaffAttendanceTab() {
  const [date, setDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchAttendance(); }, [date]);

  const fetchAttendance = async () => {
    setLoading(true);
    try {
      const res = await rmAPI.getStaffAttendance(date);
      setStaff(res.data.staff || []);
    } catch { toast.error('Failed to load attendance'); }
    finally { setLoading(false); }
  };

  const changeDate = (dir) => {
    const d = new Date(date);
    d.setDate(d.getDate() + dir);
    setDate(d.toISOString().split('T')[0]);
  };

  const checkedIn = staff.filter(s => s.checked_in).length;
  const dateLabel = new Date(date + 'T00:00:00').toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="text-base font-bold text-[#1a2529]">Staff Attendance</h2>
        <div className="flex items-center gap-1 bg-white rounded-xl border px-2 py-1">
          <button data-testid="att-prev-date" onClick={() => changeDate(-1)} className="h-7 w-7 rounded-lg flex items-center justify-center hover:bg-[#f4f4f5]">
            <ChevronDown size={14} className="rotate-90" />
          </button>
          <input data-testid="att-date-picker" type="date" value={date} onChange={(e) => setDate(e.target.value)} className="h-7 border-0 text-xs font-semibold text-center bg-transparent outline-none" />
          <button data-testid="att-next-date" onClick={() => changeDate(1)} className="h-7 w-7 rounded-lg flex items-center justify-center hover:bg-[#f4f4f5]">
            <ChevronDown size={14} className="-rotate-90" />
          </button>
        </div>
      </div>

      <div className="text-xs text-[#1a2529]/50 font-medium">{dateLabel} &middot; {checkedIn}/{staff.length} checked in</div>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="animate-spin text-[#026536]" size={24} /></div>
      ) : staff.length === 0 ? (
        <div className="bg-white rounded-xl p-8 text-center border border-border/30 text-[#1a2529]/40 text-sm">No employees assigned</div>
      ) : (
        <div className="space-y-2">
          {staff.map(emp => (
            <div key={emp.id} data-testid={`att-staff-${emp.id}`} className="bg-white rounded-xl p-4 border border-border/30 shadow-sm">
              <div className="flex items-start justify-between mb-2">
                <div>
                  <p className="font-bold text-sm text-[#1a2529]">{emp.name}</p>
                  <p className="text-[10px] text-[#1a2529]/40">{emp.phone}{emp.designation ? ` | ${emp.designation}` : ''}</p>
                </div>
                <Badge data-testid={`att-status-${emp.id}`} className={`border-0 text-[10px] font-bold ${emp.checked_in ? 'bg-[#026536]/10 text-[#026536]' : 'bg-red-50 text-red-400'}`}>
                  {emp.checked_in ? 'Present' : 'Absent'}
                </Badge>
              </div>

              {/* Check-in / Check-out times */}
              <div className="flex gap-4 mb-2">
                <div className="flex items-center gap-1.5">
                  <LogIn size={13} className={emp.checkin_time ? (emp.is_late ? 'text-red-400' : 'text-[#026536]') : 'text-[#1a2529]/20'} />
                  <span className={`text-xs font-semibold ${emp.checkin_time ? (emp.is_late ? 'text-red-500' : 'text-[#1a2529]') : 'text-[#1a2529]/30'}`}>
                    {emp.checkin_time || '--:--'}
                  </span>
                  {emp.is_late && <span className="text-[9px] text-red-400 font-bold">LATE</span>}
                </div>
                <div className="flex items-center gap-1.5">
                  <LogOutIcon size={13} className={emp.checkout_time ? 'text-[#026536]' : 'text-[#1a2529]/20'} />
                  <span className={`text-xs font-semibold ${emp.checkout_time ? 'text-[#1a2529]' : 'text-[#1a2529]/30'}`}>
                    {emp.checkout_time || '--:--'}
                  </span>
                </div>
              </div>

              {/* Leave balance + work permits */}
              <div className="flex items-center gap-3 pt-2 border-t border-border/10">
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-[10px] font-bold text-[#1a2529]/40 uppercase">Leaves</span>
                    <span className={`text-[10px] font-bold ${emp.leave_balance <= 2 ? 'text-red-500' : 'text-[#026536]'}`}>
                      {emp.leave_balance}/{emp.total_leaves}
                    </span>
                  </div>
                  <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                    <div className={`h-full rounded-full ${emp.leave_balance <= 2 ? 'bg-red-400' : emp.leave_balance <= 5 ? 'bg-[#dea439]' : 'bg-[#026536]'}`}
                      style={{ width: `${Math.max(0, Math.min(100, (emp.leave_balance / emp.total_leaves) * 100))}%` }} />
                  </div>
                  {emp.pending_leaves > 0 && (
                    <span className="text-[9px] text-[#dea439] font-semibold mt-0.5 block">{emp.pending_leaves} pending</span>
                  )}
                </div>
                <div className="text-center px-3 border-l border-border/10">
                  <p className="text-sm font-extrabold text-[#1a2529]">{emp.permits_used}</p>
                  <p className="text-[9px] text-[#1a2529]/40 font-bold uppercase">Permits</p>
                  {emp.permits_hours > 0 && <p className="text-[9px] text-[#1a2529]/30">{emp.permits_hours}h</p>}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


function ReportsTab() {
  const [month, setMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  useEffect(() => { fetchData(); }, [month]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await rmAPI.getApprovals(month);
      setData(res.data);
    } catch { toast.error('Failed to load reports'); }
    finally { setLoading(false); }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await rmAPI.exportApprovals(month);
      const blob = new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `rm_approvals_${month}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Report downloaded');
    } catch { toast.error('Export failed'); }
    finally { setExporting(false); }
  };

  const changeMonth = (dir) => {
    const [y, m] = month.split('-').map(Number);
    const d = new Date(y, m - 1 + dir, 1);
    setMonth(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
  };

  const s = data?.summary || {};
  const monthLabel = (() => {
    const [y, m] = month.split('-');
    return new Date(+y, +m - 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  })();

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="text-base font-bold text-[#1a2529]">Approval Reports</h2>
        <div className="flex items-center gap-2">
          <Button data-testid="rm-export-btn" onClick={handleExport} disabled={exporting || loading}
            className="bg-[#026536] hover:bg-[#026536]/90 text-xs h-9 gap-1.5 font-semibold">
            {exporting ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
            Export Excel
          </Button>
          <div className="flex items-center gap-1 bg-white rounded-xl border px-2 py-1">
            <button onClick={() => changeMonth(-1)} className="h-7 w-7 rounded-lg flex items-center justify-center hover:bg-[#f4f4f5]">
              <ChevronDown size={14} className="rotate-90" />
            </button>
            <Input data-testid="rm-report-month" type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="h-7 w-36 border-0 text-xs font-semibold text-center" />
            <button onClick={() => changeMonth(1)} className="h-7 w-7 rounded-lg flex items-center justify-center hover:bg-[#f4f4f5]">
              <ChevronDown size={14} className="-rotate-90" />
            </button>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="animate-spin text-[#026536]" size={24} /></div>
      ) : (
        <>
          <div className="grid grid-cols-3 gap-3">
            <div className="bg-white rounded-xl p-4 border border-border/40">
              <p className="text-2xl font-extrabold text-[#026536]">{s.total_leaves_approved || 0}</p>
              <p className="text-[10px] font-bold uppercase text-[#1a2529]/40">Leaves Approved</p>
              <p className="text-[10px] text-red-400 mt-0.5">{s.total_leaves_rejected || 0} rejected</p>
            </div>
            <div className="bg-white rounded-xl p-4 border border-border/40">
              <p className="text-2xl font-extrabold text-[#026536]">{s.total_sanctions_approved || 0}</p>
              <p className="text-[10px] font-bold uppercase text-[#1a2529]/40">Sanctions Approved</p>
              <p className="text-[10px] text-red-400 mt-0.5">{s.total_sanctions_rejected || 0} rejected</p>
            </div>
            <div className="bg-white rounded-xl p-4 border border-border/40">
              <p className="text-2xl font-extrabold text-[#026536]">{s.total_permits_approved || 0}</p>
              <p className="text-[10px] font-bold uppercase text-[#1a2529]/40">Permits Approved</p>
              <p className="text-[10px] text-red-400 mt-0.5">{s.total_permits_rejected || 0} rejected</p>
            </div>
          </div>

          {/* Leaves Table */}
          {(data?.leaves?.length > 0) && (
            <div className="bg-white rounded-xl border border-border/40 overflow-hidden">
              <div className="px-4 py-3 border-b bg-[#f8f8f8]">
                <h3 className="text-xs font-bold text-[#1a2529]/70">Leave Decisions ({data.leaves.length})</h3>
              </div>
              <div className="divide-y">
                {data.leaves.map(lv => (
                  <div key={lv.id} className="px-4 py-2.5 flex items-center justify-between text-xs">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="h-7 w-7 rounded-full bg-[#f4f4f5] flex items-center justify-center text-[10px] font-bold text-[#1a2529]/40 shrink-0">{lv.user_name?.[0]}</div>
                      <div className="min-w-0">
                        <p className="font-semibold text-[#1a2529] truncate">{lv.user_name}</p>
                        <p className="text-[10px] text-[#1a2529]/40">{lv.leave_type} | {lv.duration_type === 'half_day' ? 'Half Day' : lv.duration_type === 'hours' ? `${lv.hours}h` : `${lv.days} day(s)`} | {formatDate(lv.start_date)}{lv.start_date !== lv.end_date ? ` - ${formatDate(lv.end_date)}` : ''}</p>
                      </div>
                    </div>
                    <Badge className={`border-0 text-[9px] font-bold capitalize ${lv.status === 'approved' ? 'bg-[#026536]/10 text-[#026536]' : 'bg-red-50 text-red-500'}`}>{lv.status}</Badge>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Sanctions Table */}
          {(data?.sanctions?.length > 0) && (
            <div className="bg-white rounded-xl border border-border/40 overflow-hidden">
              <div className="px-4 py-3 border-b bg-[#f8f8f8]">
                <h3 className="text-xs font-bold text-[#1a2529]/70">Late Sanction Decisions ({data.sanctions.length})</h3>
              </div>
              <div className="divide-y">
                {data.sanctions.map(s => (
                  <div key={s.id} className="px-4 py-2.5 flex items-center justify-between text-xs">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="h-7 w-7 rounded-full bg-[#f4f4f5] flex items-center justify-center text-[10px] font-bold text-[#1a2529]/40 shrink-0">{s.user_name?.[0]}</div>
                      <div className="min-w-0">
                        <p className="font-semibold text-[#1a2529] truncate">{s.user_name}</p>
                        <p className="text-[10px] text-[#1a2529]/40">{formatDate(s.date)} | {s.checkin_time || 'N/A'}</p>
                      </div>
                    </div>
                    <Badge className={`border-0 text-[9px] font-bold capitalize ${s.status === 'approved' ? 'bg-[#026536]/10 text-[#026536]' : 'bg-red-50 text-red-500'}`}>{s.status}</Badge>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Permits Table */}
          {(data?.permits?.length > 0) && (
            <div className="bg-white rounded-xl border border-border/40 overflow-hidden">
              <div className="px-4 py-3 border-b bg-[#f8f8f8]">
                <h3 className="text-xs font-bold text-[#1a2529]/70">Work Permit Decisions ({data.permits.length})</h3>
              </div>
              <div className="divide-y">
                {data.permits.map(p => (
                  <div key={p.id} className="px-4 py-2.5 flex items-center justify-between text-xs">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="h-7 w-7 rounded-full bg-[#f4f4f5] flex items-center justify-center text-[10px] font-bold text-[#1a2529]/40 shrink-0">{p.user_name?.[0]}</div>
                      <div className="min-w-0">
                        <p className="font-semibold text-[#1a2529] truncate">{p.user_name}</p>
                        <p className="text-[10px] text-[#1a2529]/40">{p.permission_type === 'wfh' ? 'Work from Home' : p.permission_type?.replace('_',' ')} | {formatDate(p.date)} | {p.hours || 8}h</p>
                      </div>
                    </div>
                    <Badge className={`border-0 text-[9px] font-bold capitalize ${p.status === 'approved' ? 'bg-[#026536]/10 text-[#026536]' : 'bg-red-50 text-red-500'}`}>{p.status}</Badge>
                  </div>
                ))}
              </div>
            </div>
          )}

          {!data?.leaves?.length && !data?.sanctions?.length && !data?.permits?.length && (
            <div className="text-center py-12 text-[#1a2529]/30 text-sm">No approval records for {monthLabel}</div>
          )}
        </>
      )}
    </div>
  );
}


function OverridesTab({ employees }) {
  const [overrides, setOverrides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ employee_id: '', date: '', reason: '', check_in_time: '', check_out_time: '' });
  const [showForm, setShowForm] = useState(false);

  useEffect(() => { fetchOverrides(); }, []);

  const fetchOverrides = async () => {
    setLoading(true);
    try {
      const res = await rmAPI.getMyOverrides();
      setOverrides(res.data.overrides || []);
    } catch { toast.error('Failed to load overrides'); }
    finally { setLoading(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.employee_id || !form.date || !form.reason.trim()) {
      toast.error('Employee, date, and reason are required');
      return;
    }
    setSubmitting(true);
    try {
      await rmAPI.createOverride(form);
      toast.success('Override submitted for admin approval');
      setForm({ employee_id: '', date: '', reason: '', check_in_time: '', check_out_time: '' });
      setShowForm(false);
      fetchOverrides();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit override');
    } finally {
      setSubmitting(false);
    }
  };

  const pending = overrides.filter(o => o.status === 'pending');
  const decided = overrides.filter(o => o.status !== 'pending');

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-bold text-sm text-[#1a2529]">Attendance Overrides (Field Duty)</h3>
        <Button
          data-testid="rm-new-override-btn"
          onClick={() => setShowForm(!showForm)}
          size="sm"
          className="bg-[#026536] hover:bg-[#026536]/90 text-xs h-8 gap-1 font-semibold"
        >
          <ClipboardEdit size={13} /> New Override
        </Button>
      </div>

      {/* Create Override Form */}
      {showForm && (
        <form data-testid="rm-override-form" onSubmit={handleSubmit} className="bg-white rounded-xl border border-border/30 shadow-sm p-4 space-y-3">
          <div>
            <Label className="text-xs font-bold text-[#1a2529]/60">Employee</Label>
            <select
              data-testid="override-employee-select"
              value={form.employee_id}
              onChange={(e) => setForm({ ...form, employee_id: e.target.value })}
              className="w-full mt-1 h-9 px-3 border border-border rounded-lg text-sm bg-white"
              required
            >
              <option value="">Select employee...</option>
              {employees.map(emp => (
                <option key={emp.id} value={emp.id}>{emp.name} ({emp.phone})</option>
              ))}
            </select>
          </div>
          <div>
            <Label className="text-xs font-bold text-[#1a2529]/60">Date</Label>
            <Input
              data-testid="override-date-input"
              type="date"
              value={form.date}
              onChange={(e) => setForm({ ...form, date: e.target.value })}
              max={new Date().toISOString().split('T')[0]}
              className="mt-1 h-9 text-sm"
              required
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs font-bold text-[#1a2529]/60">Check-in Time (optional)</Label>
              <Input
                data-testid="override-checkin-time"
                type="time"
                value={form.check_in_time}
                onChange={(e) => setForm({ ...form, check_in_time: e.target.value })}
                className="mt-1 h-9 text-sm"
              />
            </div>
            <div>
              <Label className="text-xs font-bold text-[#1a2529]/60">Check-out Time (optional)</Label>
              <Input
                data-testid="override-checkout-time"
                type="time"
                value={form.check_out_time}
                onChange={(e) => setForm({ ...form, check_out_time: e.target.value })}
                className="mt-1 h-9 text-sm"
              />
            </div>
          </div>
          <div>
            <Label className="text-xs font-bold text-[#1a2529]/60">Reason</Label>
            <textarea
              data-testid="override-reason-input"
              value={form.reason}
              onChange={(e) => setForm({ ...form, reason: e.target.value })}
              placeholder="e.g., Employee was on field duty with no network..."
              className="w-full mt-1 h-20 px-3 py-2 border border-border rounded-lg text-sm resize-none"
              required
            />
          </div>
          <div className="flex gap-2 pt-1">
            <Button data-testid="override-submit-btn" type="submit" disabled={submitting} className="bg-[#026536] hover:bg-[#026536]/90 text-xs h-9 font-semibold gap-1.5">
              {submitting ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />}
              Submit for Approval
            </Button>
            <Button type="button" variant="outline" onClick={() => setShowForm(false)} className="text-xs h-9">Cancel</Button>
          </div>
        </form>
      )}

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="animate-spin text-[#026536]" size={24} /></div>
      ) : (
        <>
          {/* Pending Overrides */}
          {pending.length > 0 && (
            <div>
              <p className="text-[10px] font-extrabold text-[#dea439] uppercase mb-2">Pending Admin Approval ({pending.length})</p>
              <div className="space-y-2">
                {pending.map(o => (
                  <div key={o.id} data-testid={`rm-override-${o.id}`} className="bg-[#dea439]/5 border border-[#dea439]/20 rounded-xl p-4">
                    <div className="flex items-center justify-between mb-2">
                      <p className="font-bold text-sm text-[#1a2529]">{o.employee_name}</p>
                      <Badge className="bg-[#dea439]/10 text-[#dea439] border-0 text-[10px]">Pending</Badge>
                    </div>
                    <p className="text-xs text-[#1a2529]/60">Date: {formatDate(o.date)}</p>
                    {o.check_in_time && <p className="text-xs text-[#1a2529]/60">Check-in: {o.check_in_time}</p>}
                    {o.check_out_time && <p className="text-xs text-[#1a2529]/60">Check-out: {o.check_out_time}</p>}
                    <p className="text-[11px] text-[#1a2529]/40 mt-1 italic">"{o.reason}"</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Decided Overrides */}
          {decided.length > 0 && (
            <div>
              <p className="text-[10px] font-bold text-[#1a2529]/30 uppercase mb-2">History ({decided.length})</p>
              <div className="space-y-1.5">
                {decided.map(o => (
                  <div key={o.id} className="bg-white rounded-xl px-3 py-2.5 border border-border/10 flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2.5 min-w-0 flex-1">
                      <div className="h-7 w-7 rounded-full bg-[#f4f4f5] flex items-center justify-center text-[10px] font-bold text-[#1a2529]/40 shrink-0">{o.employee_name?.[0]}</div>
                      <div className="min-w-0 flex-1">
                        <p className="text-xs font-semibold text-[#1a2529] truncate">{o.employee_name}</p>
                        <p className="text-[10px] text-[#1a2529]/30">{formatDate(o.date)}</p>
                      </div>
                    </div>
                    <Badge className={`border-0 text-[9px] font-bold capitalize shrink-0 ${o.status === 'approved' ? 'bg-[#026536]/10 text-[#026536]' : 'bg-red-50 text-red-500'}`}>
                      {o.status}
                    </Badge>
                  </div>
                ))}
              </div>
            </div>
          )}

          {overrides.length === 0 && (
            <div className="bg-white rounded-xl p-8 text-center border border-border/30">
              <ClipboardEdit className="mx-auto text-[#1a2529]/15 mb-2" size={28} />
              <p className="text-sm font-medium text-[#1a2529]/40">No override requests yet</p>
              <p className="text-[11px] text-[#1a2529]/30 mt-1">Use "New Override" to submit backdated attendance for field staff</p>
            </div>
          )}
        </>
      )}
    </div>
  );
}


function CompOffApprovalSection({ pending, all, onAction, actionLoading }) {
  const decided = (all || []).filter(r => r.status !== 'pending');
  const formatH = (h) => h === 8 ? '1 day' : h === 4 ? 'Half day' : `${h}h`;
  return (
    <div className="space-y-4">
      <h3 className="font-bold text-sm text-[#1a2529]">Comp-Off Requests</h3>
      {pending.length > 0 && (
        <div>
          <p className="text-[10px] font-extrabold text-[#dea439] uppercase mb-2">Pending ({pending.length})</p>
          <div className="space-y-2">
            {pending.map(r => (
              <div key={r.id} data-testid={`rm-comp-off-${r.id}`} className="bg-white rounded-xl p-4 border border-border/20 shadow-sm">
                <div className="flex items-center justify-between mb-1">
                  <p className="font-bold text-sm text-[#1a2529]">{r.user_name}</p>
                  <Badge className="bg-[#026536]/10 text-[#026536] border-0 text-xs font-bold">{formatH(r.hours)}</Badge>
                </div>
                <p className="text-xs text-[#1a2529]/60">Date: {formatDate(r.date)}</p>
                {r.reason && <p className="text-[11px] text-[#1a2529]/40 mt-1 italic">"{r.reason}"</p>}
                <div className="flex gap-2 mt-3 pt-2 border-t border-border/10">
                  <Button size="sm" onClick={() => onAction('comp_off', r.id, 'approved')} disabled={!!actionLoading}
                    className="bg-[#026536] hover:bg-[#026536]/90 text-xs h-8 gap-1 font-semibold">
                    {actionLoading === `comp_off-${r.id}-approved` ? <Loader2 size={12} className="animate-spin" /> : <CheckCircle2 size={12} />} Approve
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => onAction('comp_off', r.id, 'rejected')} disabled={!!actionLoading}
                    className="text-red-500 border-red-200 hover:bg-red-50 text-xs h-8 gap-1 font-semibold">
                    {actionLoading === `comp_off-${r.id}-rejected` ? <Loader2 size={12} className="animate-spin" /> : <XCircle size={12} />} Reject
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      {decided.length > 0 && (
        <div>
          <p className="text-[10px] font-bold text-[#1a2529]/30 uppercase mb-2">History ({decided.length})</p>
          <div className="space-y-1.5">
            {decided.map(r => (
              <div key={r.id} className="bg-white rounded-xl px-3 py-2.5 border border-border/10 flex items-center justify-between gap-2">
                <div className="flex items-center gap-2.5 min-w-0 flex-1">
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-semibold text-[#1a2529] truncate">{r.user_name}</p>
                    <p className="text-[10px] text-[#1a2529]/30">{formatDate(r.date)} | {formatH(r.hours)}</p>
                  </div>
                </div>
                <Badge className={`border-0 text-[9px] font-bold capitalize shrink-0 ${r.status === 'approved' ? 'bg-[#026536]/10 text-[#026536]' : 'bg-red-50 text-red-500'}`}>{r.status}</Badge>
              </div>
            ))}
          </div>
        </div>
      )}
      {pending.length === 0 && decided.length === 0 && (
        <div className="bg-white rounded-xl p-8 text-center border border-border/30">
          <Timer className="mx-auto text-[#1a2529]/15 mb-2" size={28} />
          <p className="text-sm font-medium text-[#1a2529]/40">No comp-off requests from your team</p>
        </div>
      )}
    </div>
  );
}
